// Referenced code from https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/src/useInteractOutside.ts#L54
// This file is forked from react-aria to account for an event occuring within a ShadowDOM.
// We modify
// Because event propagation in shadowDOM bubbles up to the shadow root, we have to
// check event.composedPath and target the element that the event actually originated from.
// If the container does contain that innermost element, then we do not close the Popover.
import { useEffect, useRef } from 'react';
import { ownerDocument } from '../../utils/domHelpers';
/**
 * Example, used in components like Dialogs and Popovers so they can close
 * when a user clicks outside them.
 */ export function useInteractOutside(props) {
    const { ref, onInteractOutside, isDisabled, onInteractOutsideStart } = props;
    const stateRef = useRef({
        isPointerDown: false,
        ignoreEmulatedMouseEvents: false,
        onInteractOutside,
        onInteractOutsideStart
    });
    const state = stateRef.current;
    state.onInteractOutside = onInteractOutside;
    state.onInteractOutsideStart = onInteractOutsideStart;
    useEffect(()=>{
        if (isDisabled) {
            return;
        }
        const onPointerDown = (e)=>{
            if (isValidEvent(e, ref) && state.onInteractOutside) {
                if (state.onInteractOutsideStart) {
                    state.onInteractOutsideStart(e);
                }
                state.isPointerDown = true;
            }
        };
        // Use pointer events if available. Otherwise, fall back to mouse and touch events.
        if (typeof PointerEvent !== 'undefined') {
            const onPointerUp = (e)=>{
                if (state.isPointerDown && state.onInteractOutside && isValidEvent(e, ref)) {
                    state.isPointerDown = false;
                    state.onInteractOutside(e);
                }
            };
            // changing these to capture phase fixed combobox
            ownerDocument(ref.current).addEventListener('pointerdown', onPointerDown, true);
            ownerDocument(ref.current).addEventListener('pointerup', onPointerUp, true);
            return ()=>{
                ownerDocument(ref.current).removeEventListener('pointerdown', onPointerDown, true);
                ownerDocument(ref.current).removeEventListener('pointerup', onPointerUp, true);
            };
        } else {
            const onMouseUp = (e)=>{
                if (state.ignoreEmulatedMouseEvents) {
                    state.ignoreEmulatedMouseEvents = false;
                } else if (state.isPointerDown && state.onInteractOutside && isValidEvent(e, ref)) {
                    state.isPointerDown = false;
                    state.onInteractOutside(e);
                }
            };
            const onTouchEnd = (e)=>{
                state.ignoreEmulatedMouseEvents = true;
                if (state.onInteractOutside && state.isPointerDown && isValidEvent(e, ref)) {
                    state.isPointerDown = false;
                    state.onInteractOutside(e);
                }
            };
            ownerDocument(ref.current).addEventListener('mousedown', onPointerDown, true);
            ownerDocument(ref.current).addEventListener('mouseup', onMouseUp, true);
            ownerDocument(ref.current).addEventListener('touchstart', onPointerDown, true);
            ownerDocument(ref.current).addEventListener('touchend', onTouchEnd, true);
            return ()=>{
                ownerDocument(ref.current).removeEventListener('mousedown', onPointerDown, true);
                ownerDocument(ref.current).removeEventListener('mouseup', onMouseUp, true);
                ownerDocument(ref.current).removeEventListener('touchstart', onPointerDown, true);
                ownerDocument(ref.current).removeEventListener('touchend', onTouchEnd, true);
            };
        }
    }, [
        ref,
        state,
        isDisabled
    ]);
}
function isValidEvent(event, ref) {
    if (event instanceof MouseEvent && event.button > 0) {
        return false;
    }
    // if the event target is no longer in the document
    if (event.target) {
        const ownerDocument = event.target.ownerDocument;
        if (!ownerDocument || !ownerDocument.documentElement.contains(event.target)) {
            return false;
        }
    }
    return ref.current && !ref.current.contains(event.target);
}

//# sourceMappingURL=useInteractOutside.js.map